import { jwtDecode } from 'jwt-decode';
import { redirect } from 'react-router-dom';

interface SessionInfo {
  sessionToken: string;
  username: string;
}

/**
 * Validates a JWT token synchronously
 */
function isValidToken(token: string | undefined): boolean {
  if (!token) return false;

  try {
    const decoded = jwtDecode(token);
    const now = Date.now();

    if (decoded.exp) {
      return decoded.exp * 1000 > now;
    }

    return false;
  } catch (error) {
    console.error('Token validation failed:', error);
    return false;
  }
}

/**
 * Gets stored session from localStorage
 */
function getStoredSession(): SessionInfo | null {
  try {
    const session = localStorage.getItem('sessionInfo');
    if (!session) return null;

    return JSON.parse(session) as SessionInfo;
  } catch (error) {
    console.error('Failed to parse stored session:', error);
    localStorage.removeItem('sessionInfo');
    return null;
  }
}

/**
 * Auth loader for React Router
 * This is a synchronous check that can be used in route loaders
 */
export function authLoader({ request }: { request: Request }) {
  const url = new URL(request.url);
  const { pathname } = url;

  // Skip auth check for login routes
  if (pathname.includes('/login')) {
    return { isAuthenticated: false, username: null };
  }

  const session = getStoredSession();

  if (!session || !isValidToken(session.sessionToken)) {
    // Clear invalid session
    if (session) {
      localStorage.removeItem('sessionInfo');
    }

    // Redirect to login with return path
    return redirect(`/login?from=${encodeURIComponent(pathname)}`);
  }

  return {
    isAuthenticated: true,
    username: session.username,
  };
}

/**
 * Public loader that doesn't require authentication
 */
export function publicLoader() {
  const session = getStoredSession();

  return {
    isAuthenticated: session ? isValidToken(session.sessionToken) : false,
    username: session?.username ?? null,
  };
}
